import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wrench, Loader2, FileText, Handshake, ListChecks, ShieldCheck, Package, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import CommunityCard from "@/components/CommunityCard";
import ActorSelector from "@/components/planner/ActorSelector";
import CorporateResult from "@/components/planner/CorporateResult";
import DocumentActions from "@/components/planner/DocumentActions";
import { api } from "@/lib/api";

const DEMO_PROBLEM = "Tumpukan sampah liar di bantaran sungai RW 05";

const QUICK_EXAMPLES = [
  { label: "Contoh Warga: Sampah liar menyumbat saluran air RT", text: "Sampah liar menumpuk dan menyumbat saluran air di lingkungan RT kami sehingga menyebabkan genangan saat hujan", actor: "Warga / Individu", budget: "" },
  { label: "Contoh Komunitas: Penghijauan & bank sampah bantaran sungai", text: "Komunitas kami ingin membuat program penghijauan dan bank sampah di bantaran sungai yang gersang dan penuh sampah plastik", actor: "Komunitas / Relawan", budget: "" },
  { label: "Contoh CSR: Alokasi Rp 50 Juta program nutrisi anak pesisir", text: "Perusahaan kami ingin mengalokasikan dana CSR Rp 50 juta untuk program perbaikan nutrisi anak-anak kampung nelayan pesisir", actor: "Perusahaan / CSR", budget: "50000000" },
];

export default function SolutionPage() {
  const [params] = useSearchParams();
  const [problem, setProblem] = useState("");
  const [actor, setActor] = useState("Komunitas / Relawan");
  const [budget, setBudget] = useState("");
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const autoRan = useRef(false);

  const generate = async (text = problem, actorOverride = actor) => {
    if (text.trim().length < 10) {
      toast.error("Jelaskan masalahnya minimal 10 karakter.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/playbook", {
        problem: text,
        actor: actorOverride,
        budget: parseInt(budget || "0", 10),
        region,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat playbook. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("demo") === "1" && !autoRan.current) {
      autoRan.current = true;
      setProblem(DEMO_PROBLEM);
      generate(DEMO_PROBLEM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
      <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-bold px-4 py-1.5 rounded-full">
        <Wrench className="w-3.5 h-3.5" /> Social Action Planner
      </span>
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mt-4">
        Punya Masalah di Lingkunganmu?<br /><span className="text-emerald-600">AI Susun Rencana Aksinya.</span>
      </h1>
      <p className="text-slate-500 mt-3 max-w-2xl">
        Mesin perencana aksi adaptif multi-sektor: dari aksi warga hingga program CSR perusahaan —
        lengkap dengan playbook, surat izin RT/RW/Kelurahan, atau draf MoU kemitraan.
      </p>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6 mt-8">
        <ActorSelector actor={actor} setActor={setActor} budget={budget} setBudget={setBudget} region={region} setRegion={setRegion} />
        <label htmlFor="problem-input" className="text-sm font-bold text-slate-700 block mb-2">
          Ceritakan masalah lingkungan atau sosial yang dihadapi dan rencanakan aksi sosial/CSR sekarang.
        </label>
        <Textarea
          id="problem-input"
          data-testid="problem-input"
          value={problem}
          onChange={(e) => setProblem(e.target.value)}
          placeholder="Ceritakan masalah lingkungan atau sosial yang dihadapi dan rencanakan aksi sosial/CSR sekarang..."
          className="rounded-xl min-h-[110px] text-base"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          {QUICK_EXAMPLES.map((ex, i) => (
            <button key={i} data-testid={`example-problem-${i}`}
              onClick={() => { setProblem(ex.text); setActor(ex.actor); setBudget(ex.budget); }}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
              {ex.label}
            </button>
          ))}
        </div>
        <Button
          data-testid="generate-playbook-btn"
          onClick={() => generate()}
          disabled={loading}
          className="w-full sm:w-auto rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 h-12 mt-5"
        >
          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> AI Menyusun Playbook...</> : <><Wrench className="w-5 h-5 mr-2" /> Buat Rencana Aksi</>}
        </Button>
      </div>

      {result && (
        <div className="mt-10 space-y-8" data-testid="playbook-result">
          <div className="bg-slate-900 rounded-2xl p-8">
            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">{result.category} · {actor}</span>
            <h2 data-testid="playbook-title" className="font-heading font-black text-2xl md:text-3xl text-white tracking-tight mt-2">{result.title}</h2>
            <p className="text-slate-300 mt-3 leading-relaxed">{result.summary}</p>
          </div>

          {result.mou_tor ? (
            <CorporateResult result={result} />
          ) : (
          <>
          <div>
            <h3 className="font-heading font-extrabold text-xl text-slate-900 mb-5 flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-emerald-600" /> Action Playbook Tahap demi Tahap
            </h3>
            <div className="grid md:grid-cols-2 gap-5">
              {result.phases.map((phase, i) => (
                <div key={i} data-testid={`playbook-phase-${i}`} className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-8 h-8 rounded-full bg-emerald-600 text-white font-black text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                    <h4 className="font-heading font-extrabold text-slate-900">{phase.name}</h4>
                  </div>
                  <ul className="space-y-2">
                    {phase.steps.map((s, j) => (
                      <li key={j} className="text-sm text-slate-600 flex gap-2 leading-relaxed">
                        <span className="text-emerald-500 font-black shrink-0">•</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {(result.equipment?.length || result.safety_notes?.length) && (
            <div className="grid md:grid-cols-2 gap-5">
              {result.equipment?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h4 className="font-heading font-extrabold text-slate-900 mb-3 flex items-center gap-2"><Package className="w-5 h-5 text-teal-600" /> Kebutuhan Logistik</h4>
                  {result.volunteers_estimate && (
                    <p data-testid="volunteers-estimate" className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 mb-3">👥 {result.volunteers_estimate}</p>
                  )}
                  <ul className="space-y-1.5">{result.equipment.map((e, i) => <li key={i} className="text-sm text-slate-600">• {e}</li>)}</ul>
                </div>
              )}
              {result.safety_notes?.length > 0 && (
                <div className="bg-rose-50 rounded-2xl border border-rose-200 p-6">
                  <h4 className="font-heading font-extrabold text-rose-800 mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-600" /> Mitigasi Risiko & Keselamatan</h4>
                  <ul className="space-y-1.5">{result.safety_notes.map((n, i) => <li key={i} className="text-sm text-rose-700">• {n}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="font-heading font-extrabold text-xl text-slate-900 mb-5 flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" /> Template Dokumen Siap Salin
            </h3>
            <Tabs defaultValue="izin">
              <TabsList className="rounded-full bg-slate-100 p-1">
                <TabsTrigger data-testid="template-izin-tab" value="izin" className="rounded-full font-bold data-[state=active]:bg-white">Surat Izin RT/RW</TabsTrigger>
                <TabsTrigger data-testid="template-kolaborasi-tab" value="kolaborasi" className="rounded-full font-bold data-[state=active]:bg-white">Surat Kolaborasi</TabsTrigger>
              </TabsList>
              {[["izin", result.surat_izin, "Surat Izin Kegiatan"], ["kolaborasi", result.surat_kolaborasi, "Surat Permohonan Kolaborasi"]].map(([key, text, label]) => (
                <TabsContent key={key} value={key} className="mt-4">
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div id="planner-document">
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">{text}</pre>
                    </div>
                    <DocumentActions text={text} summary={`${result.title} — ${label}`} />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
          </>
          )}

          <div>
            <h3 className="font-heading font-extrabold text-xl text-slate-900 mb-5 flex items-center gap-2">
              <Handshake className="w-5 h-5 text-emerald-600" /> Mitra Kolaborasi yang Relevan
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-directory">
              {result.partners.map((p) => <CommunityCard key={p.id} community={p} />)}
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
            <p className="text-sm text-emerald-800 leading-relaxed">
              Playbook ini dibuat oleh AI berdasarkan praktik terbaik aksi komunitas di Indonesia. Selalu koordinasikan dengan pengurus RT/RW dan pihak berwenang setempat sebelum eksekusi.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
